/**
 * URL-persisted state for a searchable, filterable, paginated list page.
 *
 * Page, page size, search term and filters all live in the query string, so a
 * filtered view is shareable, survives a reload, and browser-back steps through
 * filter changes the way a user expects.
 *
 * The search box is debounced: `searchInput` updates on every keystroke for a
 * responsive field, while the committed value only reaches the URL (and so the
 * query key, and so the network) once typing pauses.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from './use-debounce';

export interface UseListUrlStateOptions<TFilterKey extends string> {
  /** Filter names to read from and write to the query string. */
  filterKeys: readonly TFilterKey[];
  initialLimit?: number;
  debounceMs?: number;
  /** Scroll back to the top when the page changes. */
  scrollToTop?: boolean;
}

export interface UseListUrlStateResult<TFilterKey extends string> {
  page: number;
  limit: number;
  /** Raw, undebounced field value — bind this to the search input. */
  searchInput: string;
  /** Debounced, committed search term — use this to build the query. */
  search: string;
  filters: Record<TFilterKey, string | undefined>;
  /** True when a search term or any filter is narrowing the list. */
  hasActiveFilters: boolean;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  setSearchInput: (value: string) => void;
  setFilter: (key: TFilterKey, value: string | undefined) => void;
  /** Clear the search term and every filter, returning to page 1. */
  clearFilters: () => void;
  /**
   * Read any other query param. For state that belongs in the URL but is not a
   * filter — a selected tab, say — so it neither lights up "Clear" nor gets
   * wiped by it.
   */
  getParam: (key: string) => string | undefined;
  /** Write any other query param, resetting to page 1. */
  setParam: (key: string, value: string | undefined) => void;
  /**
   * Write several params at once, resetting to page 1.
   *
   * Use this rather than calling `setParam`/`setFilter` twice in a row.
   * `setSearchParams` navigates immediately instead of queueing like a React
   * state setter, so two calls in one handler both compute from the same
   * pre-update params and the second silently discards the first.
   */
  setParams: (patch: Record<string, string | undefined>) => void;
}

const parsePositiveInt = (value: string | null, fallback: number): number => {
  const parsed = Number.parseInt(value ?? '', 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

export function useListUrlState<TFilterKey extends string>({
  filterKeys,
  initialLimit = 20,
  debounceMs = 300,
  scrollToTop = true,
}: UseListUrlStateOptions<TFilterKey>): UseListUrlStateResult<TFilterKey> {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = parsePositiveInt(searchParams.get('page'), 1);
  const limit = parsePositiveInt(searchParams.get('limit'), initialLimit);
  const committedSearch = searchParams.get('search') ?? '';

  // The field is local so typing stays instant; the URL holds only the
  // committed term. Seeded from the URL so a shared link populates the box.
  const [searchInput, setSearchInput] = useState(committedSearch);
  const debouncedSearch = useDebounce(searchInput, debounceMs);

  /**
   * Write a set of params, dropping any whose value is empty.
   *
   * `replace` controls whether the change enters browser history. Discrete
   * actions — picking a filter, changing page — push, so Back steps back
   * through them. The debounced search term replaces instead: it commits on
   * every typing pause, and pushing there would bury the previous page under a
   * string of half-typed entries.
   */
  const patchParams = useCallback(
    (patch: Record<string, string | undefined>, { replace = false }: { replace?: boolean } = {}) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          for (const [key, value] of Object.entries(patch)) {
            if (value == null || value === '') next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace }
      );
    },
    [setSearchParams]
  );

  // Commit the debounced term and the page reset in ONE update. Resetting the
  // page in a separate effect would fire a request for page N of the new search
  // before the reset landed.
  //
  // The guard tracks the previous DEBOUNCED value, not the URL's. react-router
  // hands back a fresh `setSearchParams` on some renders, which changes
  // `patchParams`'s identity and re-runs this effect; keyed off the URL instead,
  // that re-run would write the still-stale debounced term back and undo a
  // Clear that had just emptied the box.
  const previousDebounced = useRef(debouncedSearch);
  useEffect(() => {
    if (debouncedSearch === previousDebounced.current) return;
    previousDebounced.current = debouncedSearch;
    patchParams({ search: debouncedSearch || undefined, page: undefined }, { replace: true });
  }, [debouncedSearch, patchParams]);

  // Keep the field in step when the term changes from outside the box —
  // browser back/forward, or a shared link.
  const previousCommitted = useRef(committedSearch);
  useEffect(() => {
    if (committedSearch === previousCommitted.current) return;
    previousCommitted.current = committedSearch;
    setSearchInput(committedSearch);
  }, [committedSearch]);

  const setPage = useCallback(
    (next: number) => {
      patchParams({ page: next > 1 ? String(next) : undefined });
      if (scrollToTop) window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [patchParams, scrollToTop]
  );

  const setLimit = useCallback(
    (next: number) => {
      // A new page size makes the current page number meaningless.
      patchParams({ limit: String(next), page: undefined });
      if (scrollToTop) window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [patchParams, scrollToTop]
  );

  const setFilter = useCallback(
    (key: TFilterKey, value: string | undefined) => {
      patchParams({ [key]: value, page: undefined });
    },
    [patchParams]
  );

  const getParam = useCallback(
    (key: string) => searchParams.get(key) ?? undefined,
    [searchParams]
  );

  const setParam = useCallback(
    (key: string, value: string | undefined) => {
      patchParams({ [key]: value, page: undefined });
    },
    [patchParams]
  );

  const setParams = useCallback(
    (patch: Record<string, string | undefined>) => {
      patchParams({ ...patch, page: undefined });
    },
    [patchParams]
  );

  const clearFilters = useCallback(() => {
    // `previousDebounced` is deliberately NOT reset here. The debounced value is
    // still the old term for another `debounceMs`, so forcing the ref to ''
    // would make the commit effect see a difference and write that old term
    // straight back. Left alone, the effect stays a no-op until the debounce
    // catches up, at which point it re-clears an already-cleared param.
    previousCommitted.current = '';
    setSearchInput('');
    patchParams({
      search: undefined,
      page: undefined,
      ...Object.fromEntries(filterKeys.map((key) => [key, undefined])),
    });
  }, [patchParams, filterKeys]);

  const filterValues = filterKeys.map((key) => searchParams.get(key) ?? undefined).join(' ');
  const filters = useMemo(
    () =>
      Object.fromEntries(filterKeys.map((key) => [key, searchParams.get(key) ?? undefined])) as Record<
        TFilterKey,
        string | undefined
      >,
    // Derived from the joined values so the object identity is stable while the
    // filters themselves are unchanged.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterValues]
  );

  const hasActiveFilters =
    searchInput !== '' || filterKeys.some((key) => searchParams.get(key) != null);

  return {
    page,
    limit,
    searchInput,
    search: committedSearch,
    filters,
    hasActiveFilters,
    setPage,
    setLimit,
    setSearchInput,
    setFilter,
    clearFilters,
    getParam,
    setParam,
    setParams,
  };
}
